import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import useTimeline from '../../modules/timeline/useTimeline'
import useSession from '../../session/useSession'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import CheckBoxesView from './View'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import {
  CheckboxesQuestionModelTypes,
  CheckboxesResponse,
  ICheckboxesQuestionSchema,
  ICheckboxesResponseValidation
} from './types'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'
import VideoBlock from '../_common/VideoBlock/VideoBlock'

const CheckboxesQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<CheckboxesQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [CheckboxesResponse, ICheckboxesResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const timeline = useTimeline()
    const [showValidation, setShowValidation] = useState(false)
    const previewOptions = usePreviewOptions()
    const [videoWatched, setVideoWatched] = useState(!model.schema.videos?.length)

    const schema = useMemo(() => {
      const schema = JSON.parse(JSON.stringify(model.schema)) as ICheckboxesQuestionSchema
      schema.answers.forEach(a => {
        a.image = imageAssetWithOptions(session.rules?.sub?.[schema.id]?.[a.id], a.image)
        a.fakeHidden =
          previewOptions.showHidden && false == session.rules?.show?.[a.id] && !previewOptions.suppressLogic
        a.hidden = !previewOptions.showHidden && false == session.rules?.show?.[a.id]
        return a
      })
      return schema
    }, [model.schema, session.rules, previewOptions.showHidden, previewOptions.suppressLogic])

    useEffect(() => {
      session.enableNext(responseValidation.valid && videoWatched)
    }, [session, responseValidation.valid, videoWatched])

    const onUpdateResponse = (response: CheckboxesResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
      // setShowValidation(false)
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(
      ref,
      () => {
        return {
          tryNext() {
            if (model.responseValidation.valid || session.skipValidation) {
              return false
            } else {
              session.enableNext(false)
              return true
            }
          },
          tryPrev() {
            return false
          },
          showValidation() {
            setShowValidation(true)
          }
        }
      },
      [model, session]
    )

    const imagesRef = useRef<string[]>(null)
    if (!imagesRef.current)
      imagesRef.current = model.schema.answers.reduce((array: string[], answer) => {
        if (model.schema.image_grid_columns || (answer?.image && !answer.image.expand))
          array.push(
            model.schema.image_grid_columns ? getMediumAssetPath(answer.image) : getSmallAssetPath(answer.image)
          )
        return array
      }, [])

    return (
      <QuestionFrame model={model} images={imagesRef.current}>
        <VideoBlock
          videos={model.schema.videos}
          media_mask={model.schema.media_mask}
          id={model.schema.id}
          showValidationError={showValidation}
          onWatched={() => setVideoWatched(true)}
        />
        <CheckBoxesView
          schema={schema}
          response={response}
          responseValidation={responseValidation}
          frame={frame}
          timeline={timeline}
          showValidation={showValidation}
          onUpdateResponse={onUpdateResponse}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

CheckboxesQuestion.displayName = 'CheckboxesQuestion'

export default CheckboxesQuestion
